import { championImageUrl } from '../config';
import type { ChampionSummary, Team } from '../types';
import { Draggable, Droppable, type Bucket } from '../dnd';
import { BAN_COUNT, TEAM_SIZE, type Mode } from '../draft';

interface Props {
  champions: Map<string, ChampionSummary>;
  allies: string[];
  enemies: string[];
  bans: string[];
  target: Bucket;
  onTargetChange: (b: Bucket) => void;
  onRemove: (id: string) => void;
  onClear: () => void;
  meIndex: number;
  onMeIndexChange: (i: number) => void;
  mode: Mode;
  onModeChange: (m: Mode) => void;
}

const TEAM_LABEL: Record<Team, string> = { ally: 'Your team', enemy: 'Enemy team' };
const TARGET_LABEL: Record<Bucket, string> = { ally: 'Your team', enemy: 'Enemy team', ban: 'Bans' };

/** One row/grid of droppable champion slots. Filled slots are draggable + removable. */
function Slots({
  bucket,
  ids,
  size,
  champions,
  onRemove,
  meIndex,
  grid,
}: {
  bucket: Bucket;
  ids: string[];
  size: number;
  champions: Map<string, ChampionSummary>;
  onRemove: (id: string) => void;
  meIndex?: number;
  grid?: boolean;
}) {
  const slots = Array.from({ length: size }, (_, i) => ids[i] ?? null);
  return (
    <div className={`slot-row slot-row--${bucket}${grid ? ' slot-row--grid' : ''}`}>
      {slots.map((id, i) => {
        const champ = id ? champions.get(id) : null;
        const url = champ ? championImageUrl(champ.image) : null;
        const isMe = meIndex === i;
        return (
          <Droppable
            key={i}
            id={`drop:${bucket}:${i}`}
            data={{ bucket, index: i }}
            className={`slot-wrap${isMe ? ' slot-wrap--me' : ''}`}
          >
            {isMe && <span className="me-badge">YOU</span>}
            {champ ? (
              <Draggable id={`slot:${bucket}:${i}`} data={{ championId: champ.id, from: bucket }}>
                <button
                  type="button"
                  className="slot slot--filled"
                  onClick={() => onRemove(champ.id)}
                  title={`${champ.name} — click to remove, drag to move`}
                >
                  {url && <img src={url} alt={champ.name} width={52} height={52} />}
                </button>
              </Draggable>
            ) : (
              <div className="slot" title="Drop a champion here">
                <span className="slot__plus">+</span>
              </div>
            )}
          </Droppable>
        );
      })}
    </div>
  );
}

/** The left tab of a row: click to make it the active target (where clicked
 * champions land). Highlights when active. */
function RowTab({
  bucket,
  label,
  isActive,
  onClick,
}: {
  bucket: Bucket;
  label: string;
  isActive: boolean;
  onClick: (b: Bucket) => void;
}) {
  const teamCls = bucket === 'ban' ? 'row-tab--ban' : `team--${bucket}`;
  return (
    <button
      type="button"
      className={`row-tab row-tab--choose ${teamCls}${isActive ? ' row-tab--current' : ''}`}
      onClick={() => onClick(bucket)}
    >
      <span className="row-tab__name">{label}</span>
      {isActive && <span className="row-tab__now">● active</span>}
    </button>
  );
}

/** The draft board: mode + active-target status, the two teams (your team carries
 * the inline player numbers), and a 2×5 ban block (Summoner's Rift only). */
export default function DraftBoard({
  champions,
  allies,
  enemies,
  bans,
  target,
  onTargetChange,
  onRemove,
  onClear,
  meIndex,
  onMeIndexChange,
  mode,
  onModeChange,
}: Props) {
  return (
    <section className="draft-board">
      <div className="draft-status">
        <div className="mode-toggle" role="group" aria-label="Game mode">
          <button
            type="button"
            className={`mode-btn${mode === 'sr' ? ' mode-btn--active' : ''}`}
            onClick={() => onModeChange('sr')}
          >
            Summoner’s Rift
          </button>
          <button
            type="button"
            className={`mode-btn${mode === 'aram' ? ' mode-btn--active' : ''}`}
            onClick={() => onModeChange('aram')}
          >
            ARAM
          </button>
        </div>
        <span className={`draft-status__text draft-status__text--${target === 'ban' ? 'ally' : target}`}>
          Adding to <strong>{TARGET_LABEL[target]}</strong> — click a row to switch
        </span>
        <button type="button" className="clear-btn" onClick={onClear}>
          Reset
        </button>
      </div>

      {/* Inline player numbers, aligned above your team's columns; click to mark yourself. */}
      <div className="team-row team-row--head">
        <div className="row-tab row-tab--spacer" />
        <div className="num-row">
          {Array.from({ length: TEAM_SIZE }, (_, i) => (
            <button
              key={i}
              type="button"
              className={`num-cell${meIndex === i ? ' num-cell--active' : ''}`}
              onClick={() => onMeIndexChange(i)}
              title={`You are player ${i + 1}`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>

      <div className={`team-row${target === 'ally' ? ' team-row--current' : ''}`}>
        <RowTab bucket="ally" label={TEAM_LABEL.ally} isActive={target === 'ally'} onClick={onTargetChange} />
        <Slots bucket="ally" ids={allies} size={TEAM_SIZE} champions={champions} onRemove={onRemove} meIndex={meIndex} />
      </div>

      <div className={`team-row${target === 'enemy' ? ' team-row--current' : ''}`}>
        <RowTab bucket="enemy" label={TEAM_LABEL.enemy} isActive={target === 'enemy'} onClick={onTargetChange} />
        <Slots bucket="enemy" ids={enemies} size={TEAM_SIZE} champions={champions} onRemove={onRemove} />
      </div>

      {mode === 'sr' && (
        <div className={`team-row team-row--bans${target === 'ban' ? ' team-row--current' : ''}`}>
          <RowTab bucket="ban" label="Bans" isActive={target === 'ban'} onClick={onTargetChange} />
          <Slots bucket="ban" ids={bans} size={BAN_COUNT} champions={champions} onRemove={onRemove} grid />
        </div>
      )}
    </section>
  );
}
